import dayjs from '@/plugins/dayjs';
import { Pausable, useIntervalFn } from '@vueuse/core';
import { DurationUnitType } from 'dayjs/plugin/duration';
import { readonly, ref } from 'vue';

export function duration(ms: number | string): string {
  try {
    const duration = dayjs.duration(+ms);

    const durations: DurationUnitType[] = ['hours', 'minutes', 'seconds'];
    let res: string = durations
      .reduce((acc: string[], v: DurationUnitType) => {
        const val = duration.get(v);
        if (isNaN(val)) throw new Error('NaN');
        return [...acc, val.toString().padStart(2, '0')];
      }, [])
      .join(':');

    const days = duration.get('days');

    if (days) {
      res = `${days} day, ${res}`;
    }

    return res;
  } catch (e) {
    return '-';
  }
}

/**
 * Converts the specified period into the format of the remaining time (Months Weeks Days Hours Minutes) relative to the start date.
 * A period of ~30 days is taken per month.
 * @param start - ms
 * @param end - ms
 * @returns string
 */
export function shortDuration(start: number, end: number): string {
  try {
    let days = 0;
    const diff = end - start;

    const duration = dayjs.duration(diff);
    const month = duration.months();
    days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();

    //special values - the idea is to simply divide the remaining days into weeks
    const DAYS_A_WEEK = 7;
    const wholePartOfWeeks = Math.floor(days / DAYS_A_WEEK);
    const weeks = wholePartOfWeeks ? wholePartOfWeeks : 0;

    if (weeks) {
      days = days % DAYS_A_WEEK;
    }

    //base values
    const monthLabel = month ? `${month}M ` : '';
    const weeksLabel = weeks ? `${weeks}W ` : '';
    const daysLabel = days ? `${days}D ` : '';
    const hoursLabel = hours ? `${hours}H ` : '';
    const minutesLabel = minutes ? `${minutes}M ` : '';

    return monthLabel + weeksLabel + daysLabel + hoursLabel + minutesLabel;
  } catch (e) {
    return '-';
  }
}

export function useNowSeconds(interval = 5000) {
  function calcNowSeconds() {
    return Math.floor(+new Date() / 1000);
  }
  const nowSeconds = ref();

  const update = () => (nowSeconds.value = calcNowSeconds());

  const controls: Pausable = useIntervalFn(update, interval, {
    immediate: true,
    immediateCallback: true,
  });

  return { nowSeconds: readonly(nowSeconds), ...controls };
}
